/**
 * Mask token characters: `9` matches a digit, `a` matches a letter, `*` matches either.
 * Any other character in a mask is a literal that is auto-inserted while typing.
 */
const TOKEN_MATCHERS: Record<string, RegExp> = {
	'9': /[0-9]/,
	a: /[a-zA-Z]/,
	'*': /[a-zA-Z0-9]/,
};

/** Returns true if the given mask character is a token slot (`9`/`a`/`*`), false for literals. */
export const isMaskToken = (char: string): boolean => char in TOKEN_MATCHERS;

/** Number of fillable (token) positions in the mask. */
export const countMaskTokens = (mask: string): number => {
	return mask.split('').filter(isMaskToken).length;
};

/**
 * Strips an arbitrary input string down to only the characters that can fill the mask's
 * token positions, in order. Literals and junk characters are dropped; extraction stops
 * once every token position could be filled.
 */
export const extractRawValue = (input: string, mask: string): string => {
	const tokens = mask.split('').filter(isMaskToken);
	let raw = '';
	for (const char of input) {
		if (raw.length >= tokens.length) {
			break;
		}
		if (TOKEN_MATCHERS[tokens[raw.length]].test(char)) {
			raw += char;
		}
	}
	return raw;
};

// ─── Placeholder-slot helpers ─────────────────────────────────────────────────

/**
 * Returns true if `char` satisfies the token type represented by `maskChar`.
 * Returns false for non-token mask characters (literals).
 */
export const matchesToken = (char: string, maskChar: string): boolean => {
	const matcher = TOKEN_MATCHERS[maskChar];
	return matcher !== undefined && matcher.test(char);
};

/**
 * Produces the normalized full-length slotted string from an arbitrary `value`.
 * The result is always `mask.length` characters long.
 *
 * When `value.length === mask.length` (already a full-length slotted string), each token
 * slot is read positionally from `value` — this preserves interior placeholder holes.
 * When `value.length < mask.length`, token characters are extracted left-to-right via
 * `extractRawValue` and filled into slots from the left.
 *
 * Literal positions always come from `mask`, regardless of what `value` has there.
 */
export const buildSlotString = (value: string, mask: string, placeholderChar: string): string => {
	const chars = mask.split('');

	if (value.length === mask.length) {
		// Positional copy: read each token slot from value directly, preserve placeholders
		return chars
			.map((maskChar, i) => {
				if (!isMaskToken(maskChar)) {
					return maskChar; // always literal from mask
				}
				const v = value[i];
				// If the value char matches the token or is the placeholder, keep it; otherwise use placeholder
				if (v === placeholderChar || matchesToken(v, maskChar)) {
					return v;
				}
				return placeholderChar;
			})
			.join('');
	}

	// Shorter value: extract raw token chars and fill left-to-right
	const raw = extractRawValue(value, mask);
	let rawIndex = 0;
	return chars
		.map(maskChar => {
			if (!isMaskToken(maskChar)) {
				return maskChar;
			}
			if (rawIndex < raw.length) {
				return raw[rawIndex++];
			}
			return placeholderChar;
		})
		.join('');
};

/**
 * Returns `''` when all token slots in `slotString` contain `placeholderChar`
 * (meaning the field is visually empty and the HTML `placeholder` should show).
 * Otherwise returns `slotString` unchanged.
 */
export const slotStringToValue = (slotString: string, mask: string, placeholderChar: string): string => {
	for (let i = 0; i < mask.length; i++) {
		if (isMaskToken(mask[i]) && slotString[i] !== placeholderChar) {
			return slotString;
		}
	}
	return '';
};

/**
 * Returns the smallest token index strictly greater than `fromExclusive`,
 * or `mask.length` if no such index exists.
 */
export const nextTokenIndex = (mask: string, fromExclusive: number): number => {
	for (let i = fromExclusive + 1; i < mask.length; i++) {
		if (isMaskToken(mask[i])) {
			return i;
		}
	}
	return mask.length;
};

/**
 * Returns the largest token index strictly less than `fromExclusive`,
 * or `-1` if no such index exists.
 */
export const prevTokenIndex = (mask: string, fromExclusive: number): number => {
	for (let i = fromExclusive - 1; i >= 0; i--) {
		if (isMaskToken(mask[i])) {
			return i;
		}
	}
	return -1;
};

/**
 * Largest token index `< fromExclusive` whose slot is filled (not `placeholderChar`), skipping
 * empty slots — so Backspace with the caret parked past trailing gaps deletes the last real
 * character instead of clearing an already-empty slot. `-1` if no filled token precedes the caret.
 */
export const prevFilledTokenIndex = (
	mask: string,
	slots: string[],
	fromExclusive: number,
	placeholderChar: string
): number => {
	for (let i = fromExclusive - 1; i >= 0; i--) {
		if (isMaskToken(mask[i]) && slots[i] !== placeholderChar) {
			return i;
		}
	}
	return -1;
};

/**
 * Returns the smallest token index `>= pos`, or `-1` if none exists at or after `pos`.
 */
export const firstTokenIndexAtOrAfter = (mask: string, pos: number): number => {
	for (let i = pos; i < mask.length; i++) {
		if (isMaskToken(mask[i])) {
			return i;
		}
	}
	return -1;
};

/**
 * Returns the smallest token index whose slot is still unfilled (holds `placeholderChar`),
 * or `-1` if every token slot is filled.
 */
export const firstEmptyTokenIndex = (mask: string, slots: string[], placeholderChar: string): number => {
	for (let i = 0; i < mask.length; i++) {
		if (isMaskToken(mask[i]) && slots[i] === placeholderChar) {
			return i;
		}
	}
	return -1;
};

/**
 * Handles a separator keystroke (e.g. `/`, `-`, `.`) typed inside a partially-filled digit
 * segment — lets users type `1/2/2000` and have it normalize to `01/02/2000`.
 *
 * Finds the all-digit (`9`) segment the caret sits in — the run of token slots ending just
 * before the next literal at or after `caret` — and, when it is partially filled, right-aligns
 * the typed digits within the segment and zero-pads the leading slots. Returns the updated
 * `slots` plus the caret position at the start of the next segment (past the separator).
 *
 * Returns `null` (no change) when there is no following literal (e.g. the trailing year
 * segment), the segment is empty, the segment is already full, or the segment contains a
 * non-digit token. `slots` is not mutated.
 */
export const padSegmentBeforeSeparator = (
	slots: string[],
	mask: string,
	caret: number,
	placeholderChar: string
): { slots: string[]; caret: number } | null => {
	// The separator commits the segment that ends at the next literal at/after the caret.
	let litIndex = -1;
	for (let i = caret; i < mask.length; i++) {
		if (!isMaskToken(mask[i])) {
			litIndex = i;
			break;
		}
	}
	if (litIndex === -1) {
		// No following literal (e.g. the trailing year segment) — nothing to commit.
		return null;
	}

	// Walk back from the slot before the literal to find the start of this contiguous segment.
	let start = litIndex - 1;
	if (start < 0 || !isMaskToken(mask[start])) {
		return null;
	}
	while (start - 1 >= 0 && isMaskToken(mask[start - 1])) {
		start--;
	}

	// Only zero-pad pure-digit segments — padding letter/alphanumeric slots makes no sense.
	for (let i = start; i < litIndex; i++) {
		if (mask[i] !== '9') {
			return null;
		}
	}

	const size = litIndex - start;
	const filled = [];
	for (let i = start; i < litIndex; i++) {
		if (slots[i] !== placeholderChar) {
			filled.push(slots[i]);
		}
	}
	// Nothing to pad if the segment is empty, and nothing to do if it is already full.
	if (filled.length === 0 || filled.length === size) {
		return null;
	}

	// Right-align the typed digits, zero-padding the leading slots.
	const padCount = size - filled.length;
	const next = slots.slice();
	for (let k = 0; k < size; k++) {
		next[start + k] = k < padCount ? '0' : filled[k - padCount];
	}

	return { slots: next, caret: nextTokenIndex(mask, litIndex) };
};

/**
 * The mask's contiguous token-segments in order — runs of token slots split by literals.
 * For `99/99/9999` → `[{ width: 2, allDigits: true }, { width: 2, … }, { width: 4, … }]`;
 * for `(999) 999-9999` → widths `[3, 3, 4]`. `allDigits` is true only when every slot in the
 * segment is a `9` token, so callers can zero-pad numeric segments without touching letter ones.
 */
export const maskTokenSegments = (mask: string): { width: number; allDigits: boolean }[] => {
	const segments: { width: number; allDigits: boolean }[] = [];
	let i = 0;
	while (i < mask.length) {
		if (!isMaskToken(mask[i])) {
			i++;
			continue;
		}
		let allDigits = true;
		let j = i;
		while (j < mask.length && isMaskToken(mask[j])) {
			if (mask[j] !== '9') {
				allDigits = false;
			}
			j++;
		}
		segments.push({ width: j - i, allDigits });
		i = j;
	}
	return segments;
};

/**
 * Normalizes pasted text whose segments are delimited by one of the mask's `separators` so an
 * unpadded date like `1/1/2000` fills correctly instead of packing left-to-right into `11/20/00`.
 *
 * Splits the paste on the separator(s), left-pads each all-digit segment with `0` up to its mask
 * segment width, and returns the segments concatenated into a single token stream — once every
 * segment is full width, the caller's left-to-right fill lands each digit in the right slot (which
 * is why an already-padded `10/10/2026` passes through unchanged). Mirrors the typing-path zero-pad
 * in `padSegmentBeforeSeparator`.
 *
 * Returns the input unchanged (falls back to the plain fill path) when there are no separators
 * configured, the paste contains no separator, the mask has no token-segments, or the paste's
 * segment count doesn't match the mask's — so partial or malformed pastes aren't mis-padded.
 */
export const padPastedSegments = (pasteText: string, mask: string, separators: readonly string[]): string => {
	if (separators.length === 0 || !separators.some(sep => pasteText.includes(sep))) {
		return pasteText;
	}
	const segments = maskTokenSegments(mask);
	if (segments.length === 0) {
		return pasteText;
	}
	// Escape every separator char for use inside a character class — note `-` must be escaped too,
	// or an unescaped `-` between two others (e.g. `[/-.]`) is parsed as an out-of-order range.
	const separatorClass = separators.map(sep => sep.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')).join('');
	const parts = pasteText.split(new RegExp(`[${separatorClass}]`));
	if (parts.length !== segments.length) {
		return pasteText;
	}
	return parts
		.map((part, index) => {
			const { width, allDigits } = segments[index];
			// Only zero-pad numeric segments holding a short, non-empty run of digits — `\d+` (not
			// `\d*`) so an empty segment (e.g. the middle of `1//2000`) is left as-is rather than
			// fabricated into `00`. Letter/alphanumeric segments and over-long values also pass through.
			if (allDigits && /^\d+$/.test(part) && part.length < width) {
				return part.padStart(width, '0');
			}
			return part;
		})
		.join('');
};
